import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { FileDown, FileSpreadsheet, Eye, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, completion, exportUrl, todayStr, SHIFTS, errMsg } from "@/lib/api";

const monthStart = () => todayStr().slice(0, 8) + "01";
const selCls = "h-10 w-full rounded-md border border-[#E2DAD0] bg-white px-3 text-sm focus:outline-none focus:border-[#C87D55]";

const Stat = ({ label, value, testid, onClick }) => (
  <div onClick={onClick} data-testid={`${testid}-card`} className={`bg-white border border-[#E2DAD0] rounded-2xl p-5 ${onClick ? "cursor-pointer hover:border-[#C87D55] hover:shadow-md transition-all duration-200" : ""}`}>
    <p className="text-xs font-medium uppercase tracking-wider text-[#666059]">{label}{onClick && <span className="normal-case tracking-normal text-[#C87D55]"> · click to view</span>}</p>
    <p className="font-heading text-3xl font-semibold text-[#2A3C36] mt-2" data-testid={testid}>{value}</p>
  </div>
);

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium uppercase tracking-wider text-[#666059]">{label}</label>
    {children}
  </div>
);

export default function HistoryPage() {
  const [f, setF] = useState({ start: monthStart(), end: todayStr(), shift: "", staff: "", status: "" });
  const [rows, setRows] = useState([]);
  const [del, setDel] = useState(null);
  const [pin, setPin] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [modal, setModal] = useState(null);

  const confirmDelete = async () => {
    if (!pin) return toast.error("Enter the delete password");
    setDeleting(true);
    try {
      await api.delete(`/checklists/${del.id}`, { headers: { "X-Delete-Pin": pin } });
      toast.success("Report deleted");
      setDel(null);
      setPin("");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setDeleting(false);
    }
  };

  const load = useCallback(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/checklists", { params }).then((r) => setRows(r.data));
  }, [f]);

  useEffect(() => { load(); }, [load]);

  const avg = rows.length ? Math.round(rows.reduce((s, r) => s + completion(r.items), 0) / rows.length) : 0;
  const pendingItems = rows.flatMap((r) => r.follow_ups.filter((x) => x.status !== "Done").map((x, i) => ({ ...x, report: r, idx: i })));
  const pending = pendingItems.length;

  return (
    <div className="space-y-8">
      <div className="rise flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#C87D55]">Summary</p>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#2A3C36] mt-1">Shift Reports</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" data-testid="history-export-excel" onClick={() => window.open(exportUrl("excel", f), "_blank")}><FileSpreadsheet className="w-4 h-4 mr-1" />Export Excel</Button>
          <Button className="rounded-full bg-[#C87D55] hover:bg-[#A85832]" data-testid="history-export-pdf" onClick={() => window.open(exportUrl("pdf", f), "_blank")}><FileDown className="w-4 h-4 mr-1" />Export PDF</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 bg-[#F4F0EA] rounded-2xl p-4 rise" data-testid="history-filters">
        <Field label="From"><Input type="date" data-testid="filter-start" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="bg-white" /></Field>
        <Field label="To"><Input type="date" data-testid="filter-end" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="bg-white" /></Field>
        <Field label="Shift">
          <select data-testid="filter-shift" value={f.shift} onChange={(e) => setF({ ...f, shift: e.target.value })} className={selCls}>
            <option value="">All shifts</option>
            {SHIFTS.map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select data-testid="filter-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={selCls}>
            <option value="">All</option>
            <option value="submitted">Submitted</option>
            <option value="draft">Draft</option>
          </select>
        </Field>
        <Field label="Staff"><Input data-testid="filter-staff" placeholder="Name" value={f.staff} onChange={(e) => setF({ ...f, staff: e.target.value })} className="bg-white" /></Field>
      </div>

      <div className="grid grid-cols-3 gap-3 rise">
        <Stat label="Reports" value={rows.length} testid="stat-total" onClick={() => setModal("reports")} />
        <Stat label="Avg completion" value={`${avg}%`} testid="stat-avg" />
        <Stat label="Pending follow-ups" value={pending} testid="stat-pending" onClick={() => setModal("pending")} />
      </div>

      <div className="bg-white border border-[#E2DAD0] rounded-2xl overflow-x-auto rise">
        <table className="w-full text-sm" data-testid="history-table">
          <thead className="bg-[#F4F0EA] text-left text-xs uppercase tracking-wider text-[#666059]">
            <tr>{["Date", "Shift", "FDA", "Tasks", "Follow-ups", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-[#F0EBE3]">
            {rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#9A9188]">No reports for these filters.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-[#FBF9F5]" data-testid={`history-row-${r.id}`}>
                <td className="px-4 py-3 whitespace-nowrap">{r.date}</td>
                <td className="px-4 py-3">{r.shift}</td>
                <td className="px-4 py-3 font-medium">{r.staff_name}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 min-w-[110px]">
                    <div className="h-1.5 w-16 rounded-full bg-[#EBE5DC] overflow-hidden"><div className="h-full bg-[#C87D55]" style={{ width: `${completion(r.items)}%` }} /></div>
                    <span>{completion(r.items)}%</span>
                  </div>
                </td>
                <td className="px-4 py-3">{r.follow_ups.length}</td>
                <td className="px-4 py-3"><span className={`px-2.5 py-1 rounded-full text-xs font-medium ${r.status === "submitted" ? "bg-[#DCFCE7] text-[#15803D]" : "bg-[#FEF3C7] text-[#B45309]"}`}>{r.status === "submitted" ? "Submitted" : "Draft"}</span></td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    <Link to={`/checklist/${r.id}`} data-testid={`history-view-${r.id}`} className="p-2 rounded-md hover:bg-[#F4F0EA] text-[#2A3C36]" title="View"><Eye className="w-4 h-4" /></Link>
                    <button data-testid={`history-pdf-${r.id}`} onClick={() => window.open(exportUrl("pdf", { id: r.id }), "_blank")} className="p-2 rounded-md hover:bg-[#F4F0EA] text-[#2A3C36]" title="PDF"><FileDown className="w-4 h-4" /></button>
                    <button data-testid={`history-delete-${r.id}`} onClick={() => { setDel(r); setPin(""); }} className="p-2 rounded-md hover:bg-[#FEE2E2] text-[#9A9188] hover:text-[#B91C1C]" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!del} onOpenChange={(open) => { if (!open) { setDel(null); setPin(""); } }}>
        <DialogContent className="sm:max-w-sm bg-white">
          <DialogHeader>
            <DialogTitle className="text-[#2A3C36]">Delete report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4" data-testid="delete-dialog">
            <p className="text-sm text-[#666059]">
              Delete the <b>{del?.shift}</b> shift report by <b>{del?.staff_name}</b> ({del?.date})? This cannot be undone.
            </p>
            <div className="space-y-1.5">
              <label className="text-xs font-medium uppercase tracking-wider text-[#666059]">Delete password</label>
              <Input type="password" data-testid="delete-pin-input" value={pin} onChange={(e) => setPin(e.target.value)} onKeyDown={(e) => e.key === "Enter" && confirmDelete()} placeholder="Enter password" className="bg-[#FBF9F5]" autoFocus />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => { setDel(null); setPin(""); }} data-testid="delete-cancel-button">Cancel</Button>
              <Button className="rounded-full bg-[#B91C1C] hover:bg-[#991B1B]" disabled={deleting} onClick={confirmDelete} data-testid="delete-confirm-button">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={modal === "reports"} onOpenChange={() => setModal(null)}>
        <DialogContent className="sm:max-w-lg bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#2A3C36]">Reports summary ({rows.length})</DialogTitle>
          </DialogHeader>
          <div className="divide-y divide-[#F0EBE3]" data-testid="reports-summary-list">
            {rows.length === 0 && <p className="py-4 text-sm text-[#9A9188]">No reports for these filters.</p>}
            {rows.map((r) => (
              <Link key={r.id} to={`/checklist/${r.id}`} data-testid={`summary-report-${r.id}`} className="flex items-center justify-between gap-3 py-3 -mx-2 px-2 rounded-lg hover:bg-[#FBF9F5]">
                <div>
                  <p className="text-sm font-medium text-[#1C201D]">{r.staff_name} · {r.shift}</p>
                  <p className="text-xs text-[#9A9188]">{r.date} · {r.follow_ups.length} follow-up(s)</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-sm text-[#666059]">{completion(r.items)}% done</span>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${r.status === "submitted" ? "bg-[#DCFCE7] text-[#15803D]" : "bg-[#FEF3C7] text-[#B45309]"}`}>{r.status === "submitted" ? "Submitted" : "Draft"}</span>
                </div>
              </Link>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={modal === "pending"} onOpenChange={() => setModal(null)}>
        <DialogContent className="sm:max-w-2xl bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#2A3C36]">Pending follow-ups ({pending})</DialogTitle>
          </DialogHeader>
          <div className="space-y-3" data-testid="pending-list">
            {pendingItems.length === 0 && <p className="py-2 text-sm text-[#9A9188]">No pending follow-ups. Great job!</p>}
            {pendingItems.map((f, i) => (
              <div key={i} className="border border-[#E2DAD0] rounded-xl p-4" data-testid={`pending-item-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="text-sm space-y-1">
                    <p className="font-medium text-[#1C201D]">{f.task_issue || "(no issue noted)"}</p>
                    <p className="text-[#666059]"><span className="text-[#9A9188]">Guest/Room:</span> {f.guest_room || "-"}{f.time ? ` · ${f.time}` : ""}</p>
                    {f.action_taken && <p className="text-[#666059]"><span className="text-[#9A9188]">Action:</span> {f.action_taken}</p>}
                  </div>
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-[#FEF3C7] text-[#B45309] shrink-0">Pending</span>
                </div>
                <div className="mt-3 flex items-center justify-between text-xs text-[#9A9188]">
                  <span>{f.report.date} · {f.report.shift} shift · {f.report.staff_name}</span>
                  <Link to={`/checklist/${f.report.id}`} data-testid={`pending-open-${i}`} className="font-medium text-[#C87D55] hover:text-[#A85832]">Open report →</Link>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
